/**
 * AnswerToolbar – Sticky toolbar at the top of the center pane.
 *
 * Controls: mode selector, inline-citations toggle, compact/comfortable toggle,
 * regenerate button, copy answer button.
 *
 * All state is lifted – the toolbar only receives props and fires callbacks.
 */
import React from 'react';
import {
    Box,
    Chip,
    ToggleButtonGroup,
    ToggleButton,
    IconButton,
    Tooltip,
    Divider,
    useMediaQuery,
} from '@mui/material';
import {
    Storage,
    TravelExplore,
    Language,
    Replay,
    FormatQuote,
    DensitySmall,
    DensityMedium,
    ContentCopy,
    History,
    Source,
    FilterList,
    NoteAlt,
} from '@mui/icons-material';
import NotesIcon from '../notes/NotesIcon';

const modeOptions = [
    { value: 'InternalOnly', label: 'Internal', icon: <Storage sx={{ fontSize: 15 }} /> },
    { value: 'Hybrid', label: 'Hybrid', icon: <TravelExplore sx={{ fontSize: 15 }} /> },
    { value: 'WebOnly', label: 'Web', icon: <Language sx={{ fontSize: 15 }} /> },
];

const AnswerToolbar = ({
    modePreference,
    onModeChange,
    inlineCitations,
    onToggleInlineCitations,
    compactMode,
    onToggleCompact,
    onRegenerate,
    onCopyAnswer,
    onSaveToNotes,
    hasAnswer,
    /* responsive drawer openers */
    onOpenHistory,
    onOpenSources,
    showHistoryBtn,
    showSourcesBtn,
    onToggleFilters,
    showFilters,
    companyLabel,
    notesBadge,
    onOpenNotes,
    useAskAiV2,
    onToggleAskAiV2,
}) => {
    const isSm = useMediaQuery('(max-width:959px)');

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: { xs: 1.5, md: 2 },
                py: 0.75,
                borderBottom: '1px solid var(--border)',
                backgroundColor: 'var(--bg-2)',
                flexWrap: 'wrap',
                position: 'sticky',
                top: 0,
                zIndex: 10,
                minHeight: 42,
            }}
        >
            {/* ── History drawer (small screens) ── */}
            {showHistoryBtn && (
                <Tooltip title="History">
                    <IconButton size="small" onClick={onOpenHistory} sx={{ color: 'var(--text-2)' }}>
                        <History fontSize="small" />
                    </IconButton>
                </Tooltip>
            )}

            {companyLabel && (
                <Chip
                    size="small"
                    label={companyLabel}
                    sx={{
                        maxWidth: 180,
                        backgroundColor: 'var(--bg-3)',
                        color: 'var(--brand-2)',
                        fontWeight: 500,
                        fontSize: '0.73rem',
                        height: 22,
                        '& .MuiChip-label': {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        },
                    }}
                />
            )}

            {/* ── Mode selector ── */}
            <ToggleButtonGroup
                exclusive
                size="small"
                value={modePreference}
                onChange={(_, v) => v && onModeChange(v)}
                sx={{
                    height: 30,
                    '& .MuiToggleButton-root': {
                        textTransform: 'none',
                        fontSize: '0.72rem',
                        px: 1.2,
                        py: 0,
                        gap: 0.5,
                        borderColor: 'var(--border)',
                        color: 'var(--text-2)',
                        '&.Mui-selected': {
                            backgroundColor: 'var(--bg-3)',
                            color: 'var(--brand-2)',
                            borderColor: 'var(--brand-2)',
                            fontWeight: 600,
                        },
                    },
                }}
            >
                {modeOptions.map((m) => (
                    <ToggleButton key={m.value} value={m.value}>
                        {m.icon}
                        {!isSm && m.label}
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

            {/* ── Inline citations toggle ── */}
            <Tooltip title={inlineCitations ? 'Hide inline citations' : 'Show inline citations'}>
                <IconButton
                    size="small"
                    onClick={onToggleInlineCitations}
                    sx={{
                        color: inlineCitations ? 'var(--brand-2)' : 'var(--muted)',
                        backgroundColor: inlineCitations ? 'var(--bg-3)' : 'transparent',
                        borderRadius: 1,
                        width: 30,
                        height: 30,
                    }}
                >
                    <FormatQuote sx={{ fontSize: 17 }} />
                </IconButton>
            </Tooltip>

            {/* ── Compact / Comfortable ── */}
            <Tooltip title={compactMode ? 'Comfortable spacing' : 'Compact spacing'}>
                <IconButton
                    size="small"
                    onClick={onToggleCompact}
                    sx={{ color: 'var(--muted)', borderRadius: 1, width: 30, height: 30 }}
                >
                    {compactMode ? <DensityMedium sx={{ fontSize: 17 }} /> : <DensitySmall sx={{ fontSize: 17 }} />}
                </IconButton>
            </Tooltip>

            {/* ── Filters toggle ── */}
            <Tooltip title="Filters">
                <IconButton
                    size="small"
                    onClick={onToggleFilters}
                    sx={{
                        color: showFilters ? 'var(--brand-2)' : 'var(--muted)',
                        backgroundColor: showFilters ? 'var(--bg-3)' : 'transparent',
                        borderRadius: 1,
                        width: 30,
                        height: 30,
                    }}
                >
                    <FilterList sx={{ fontSize: 17 }} />
                </IconButton>
            </Tooltip>

            {/* ── Spacer ── */}
            <Box sx={{ flex: 1 }} />

            {onOpenNotes && (
                <NotesIcon badge={notesBadge} onClick={onOpenNotes} />
            )}
            {onToggleAskAiV2 && (
                <Tooltip title={useAskAiV2 ? 'Ask AI v2 (web+internal)' : 'Legacy v1 (internal only)'}>
                    <Chip
                        size="small"
                        label={useAskAiV2 ? 'v2' : 'v1'}
                        clickable
                        onClick={onToggleAskAiV2}
                        sx={{
                            height: 20,
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            backgroundColor: useAskAiV2 ? 'var(--bg-3)' : 'var(--bg-2)',
                            color: useAskAiV2 ? 'var(--brand-2)' : 'var(--muted)',
                        }}
                    />
                </Tooltip>
            )}

            {/* ── Action buttons ── */}
            {hasAnswer && (
                <>
                    <Tooltip title="Save to Tab">
                        <IconButton size="small" onClick={onSaveToNotes} sx={{ color: 'var(--text-2)' }}>
                            <NoteAlt sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Copy answer">
                        <IconButton size="small" onClick={onCopyAnswer} sx={{ color: 'var(--text-2)' }}>
                            <ContentCopy sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Regenerate">
                        <IconButton size="small" onClick={onRegenerate} sx={{ color: 'var(--text-2)' }}>
                            <Replay sx={{ fontSize: 17 }} />
                        </IconButton>
                    </Tooltip>
                </>
            )}

            {/* ── Sources drawer (small screens) ── */}
            {showSourcesBtn && (
                <Tooltip title="Sources">
                    <IconButton size="small" onClick={onOpenSources} sx={{ color: 'var(--text-2)' }}>
                        <Source fontSize="small" />
                    </IconButton>
                </Tooltip>
            )}
        </Box>
    );
};

export default AnswerToolbar;
